import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { api } from '../../utils/api';

// ── Types ─────────────────────────────────────────────────────────────────────
interface CycleStatus {
  purchased: number;
  used: number;
  remaining: number;
  isLocked: boolean;
  lockReason: string | null;
}

interface ChapterScore {
  chapter_code: string;
  chapter_name: string;
  compliance_percentage: number;
  meets_minimum_threshold: boolean;
  compliant_count: number;
  partial_count: number;
  non_compliant_count: number;
  na_count?: number;
  total_applicable_oes?: number;
  total_score?: number;        // SUM of OE scores for this chapter
  max_possible_score?: number; // SUM of max possible scores for this chapter
}

interface ComplianceDistribution {
  compliant: number;
  partial: number;
  non_compliant: number;
  na: number;
  total: number;
}

export interface IterationOption {
  iteration_id: string;
  iteration_name: string;
  iteration_number: number;
  status: string;
  overall_compliance_pct: number | null;
  created_at: string;
}

interface DashboardState {
  organization: { name: string; plan: string } | null;
  cycleStatus: CycleStatus | null;
  latestIteration: any | null;
  overallCompliance: {
    percentage: number;
    isOnTrack: boolean;
    failingChapters: { code: string; name: string; pct: number }[];
  } | null;
  chapterScores: ChapterScore[];
  complianceDistribution: ComplianceDistribution | null;
  gaps: { totalOpen: number; critical: number; major: number; minor: number } | null;
  capa: { totalOpen: number; overdue: number; open: number; inProgress: number } | null;
  recentAlerts: any[];
  kpiSummary: any[];
  // ── Iteration selector ──────────────────────────────────────────────────
  iterations: IterationOption[];
  selectedIterationId: string | null;
  iterationsLoading: boolean;
  // ── Chapter coverage ────────────────────────────────────────────────────
  totalChapters: number;
  assessedChapters: number;
  // ── Loading / error ─────────────────────────────────────────────────────
  isLoading: boolean;
  error: string | null;
  lastFetched: string | null;
}

const FALLBACK_CHAPTER_SCORES: ChapterScore[] = [
  { chapter_code: 'AAC', chapter_name: 'Access, Assessment & Continuity', compliance_percentage: 0, meets_minimum_threshold: false, compliant_count: 0, partial_count: 0, non_compliant_count: 0 },
  { chapter_code: 'COP', chapter_name: 'Care of Patients',                compliance_percentage: 0, meets_minimum_threshold: false, compliant_count: 0, partial_count: 0, non_compliant_count: 0 },
  { chapter_code: 'MOM', chapter_name: 'Management of Medication',         compliance_percentage: 0, meets_minimum_threshold: false, compliant_count: 0, partial_count: 0, non_compliant_count: 0 },
  { chapter_code: 'PRE', chapter_name: 'Patient Rights & Education',       compliance_percentage: 0, meets_minimum_threshold: false, compliant_count: 0, partial_count: 0, non_compliant_count: 0 },
  { chapter_code: 'IPC', chapter_name: 'Infection Prevention & Control',   compliance_percentage: 0, meets_minimum_threshold: false, compliant_count: 0, partial_count: 0, non_compliant_count: 0 },
  { chapter_code: 'PSQ', chapter_name: 'Patient Safety & Quality',         compliance_percentage: 0, meets_minimum_threshold: false, compliant_count: 0, partial_count: 0, non_compliant_count: 0 },
  { chapter_code: 'ROM', chapter_name: 'Responsibility of Management',     compliance_percentage: 0, meets_minimum_threshold: false, compliant_count: 0, partial_count: 0, non_compliant_count: 0 },
  { chapter_code: 'FMS', chapter_name: 'Facility Management & Safety',     compliance_percentage: 0, meets_minimum_threshold: false, compliant_count: 0, partial_count: 0, non_compliant_count: 0 },
  { chapter_code: 'HRM', chapter_name: 'Human Resource Management',        compliance_percentage: 0, meets_minimum_threshold: false, compliant_count: 0, partial_count: 0, non_compliant_count: 0 },
  { chapter_code: 'IMS', chapter_name: 'Information Management System',    compliance_percentage: 0, meets_minimum_threshold: false, compliant_count: 0, partial_count: 0, non_compliant_count: 0 },
];

const initialState: DashboardState = {
  organization: null,
  cycleStatus: null,
  latestIteration: null,
  overallCompliance: null,
  chapterScores: [],
  complianceDistribution: null,
  gaps: null,
  capa: null,
  recentAlerts: [],
  kpiSummary: [],
  iterations: [],
  selectedIterationId: null,
  iterationsLoading: false,
  totalChapters: 10,
  assessedChapters: 0,
  isLoading: false,
  error: null,
  lastFetched: null,
};

// ── Thunks ────────────────────────────────────────────────────────────────────

/**
 * Fetch iterations list AND immediately fetch dashboard data for the best
 * default iteration — all in a single thunk so there is no race condition
 * between "which iteration is selected" and "which data is displayed".
 *
 * Previously: fetchIterations set selectedIterationId, then a useEffect
 * re-ran load() — but fetchDashboardData had already been called on mount
 * with iterationId=null (server default), so the charts showed server-default
 * data while the dropdown showed the auto-selected iteration. They could differ.
 *
 * Now: we resolve the best iteration here, set selectedIterationId and fetch
 * data for it atomically. Dashboard.tsx only needs to dispatch this once.
 */
export const fetchIterationsAndDashboard = createAsyncThunk(
  'dashboard/fetchIterationsAndDashboard',
  async (_, { rejectWithValue }) => {
    try {
      const iterRes = await api.get('/dashboard/iterations');
      const iterations: IterationOption[] = iterRes.data.data ?? [];

      // Pick best default: IN_PROGRESS first, then most recent (index 0, server orders DESC)
      const best =
        iterations.find(i => i.status === 'IN_PROGRESS') ||
        iterations.find(i => i.status === 'PLANNING')    ||
        iterations[0] ||
        null;

      const iterationId = best?.iteration_id ?? null;

      const params = iterationId ? { params: { iterationId } } : {};
      const dashRes = await api.get('/dashboard/summary', params);

      return {
        iterations,
        selectedIterationId: iterationId,
        dashboard: dashRes.data.data,
      };
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to load dashboard');
    }
  }
);

/**
 * Fetch dashboard data for a specific iteration.
 * Called when the user manually switches the iteration dropdown,
 * or when the polling interval fires.
 */
export const fetchDashboardData = createAsyncThunk(
  'dashboard/fetchSummary',
  async (iterationId: string | null | undefined, { rejectWithValue }) => {
    try {
      const params = iterationId ? { params: { iterationId } } : {};
      const response = await api.get('/dashboard/summary', params);
      return { iterationId: iterationId ?? null, dashboard: response.data.data };
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to load dashboard data');
    }
  }
);

// Keep fetchIterations exported so other components (e.g. GapDashboard) can
// still call it independently if needed.
export const fetchIterations = createAsyncThunk(
  'dashboard/fetchIterations',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/dashboard/iterations');
      return response.data.data as IterationOption[];
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to load iterations');
    }
  }
);

// ── Helper: apply dashboard payload to state ──────────────────────────────────
function applyDashboardPayload(state: DashboardState, d: any) {
  state.organization      = d.organization     ?? state.organization;
  state.cycleStatus       = d.cycleStatus       ?? state.cycleStatus;
  state.latestIteration   = d.latestIteration   ?? state.latestIteration;
  state.overallCompliance = d.overallCompliance ?? state.overallCompliance;

  // Real server data — never fall back to fake fallback on success
  state.chapterScores = Array.isArray(d.chapterScores) && d.chapterScores.length > 0
    ? d.chapterScores
    : [];

  state.complianceDistribution =
    d.complianceDistribution != null && d.complianceDistribution.total > 0
      ? d.complianceDistribution
      : { compliant: 0, partial: 0, non_compliant: 0, na: 0, total: 0 };

  state.totalChapters    = d.totalChapters    ?? state.totalChapters;
  state.assessedChapters = d.assessedChapters ?? state.assessedChapters;
  state.gaps             = d.gaps             ?? state.gaps;
  state.capa             = d.capa             ?? state.capa;
  state.recentAlerts     = d.recentAlerts     ?? [];
  state.kpiSummary       = d.kpiSummary       ?? [];
  state.lastFetched      = new Date().toISOString();
  state.isLoading        = false;
  state.error            = null;
}

// ── Slice ─────────────────────────────────────────────────────────────────────
const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    clearDashboardError: (state) => { state.error = null; },
    /**
     * Called by the dropdown onChange BEFORE dispatching fetchDashboardData.
     * Clears stale chart data immediately so the old iteration's bars never
     * linger under the new iteration's header while the fetch is in-flight.
     */
    setSelectedIteration: (state, action: { payload: string }) => {
      state.selectedIterationId    = action.payload;
      state.chapterScores          = [];
      state.complianceDistribution = null;
      state.overallCompliance      = null;
      state.latestIteration        = null;
    },
  },
  extraReducers: (builder) => {

    // ── fetchIterationsAndDashboard (initial load) ───────────────────────
    builder
      .addCase(fetchIterationsAndDashboard.pending, (state) => {
        state.isLoading        = true;
        state.iterationsLoading = true;
        state.error            = null;
        // NOTE: do NOT wipe chapterScores/overallCompliance here.
        // Keeping previous values prevents the stat card from flickering to 0.0%
        // during refresh. They are overwritten in .fulfilled once real data arrives.
      })
      .addCase(fetchIterationsAndDashboard.fulfilled, (state, action) => {
        const { iterations, selectedIterationId, dashboard } = action.payload;
        state.iterationsLoading = false;
        state.iterations        = iterations;
        // Only update selectedIterationId if the user hasn't already picked one
        if (!state.selectedIterationId && selectedIterationId) {
          state.selectedIterationId = selectedIterationId;
        }
        applyDashboardPayload(state, dashboard);
      })
      .addCase(fetchIterationsAndDashboard.rejected, (state, action) => {
        state.isLoading         = false;
        state.iterationsLoading = false;
        state.error             = action.payload as string;
        if (state.chapterScores.length === 0) state.chapterScores = FALLBACK_CHAPTER_SCORES;
        if (!state.complianceDistribution) state.complianceDistribution = { compliant: 0, partial: 0, non_compliant: 0, na: 0, total: 0 };
      });

    // ── fetchDashboardData (iteration switch + polling) ──────────────────
    builder
      .addCase(fetchDashboardData.pending, (state) => {
        state.isLoading = true;
        state.error     = null;
        // NOTE: do NOT wipe chapterScores/overallCompliance here.
        // These are cleared by setSelectedIteration (on explicit dropdown change)
        // so stale bars from a different iteration are removed immediately.
        // For same-iteration refreshes and polling we want to keep previous data visible.
      })
      .addCase(fetchDashboardData.fulfilled, (state, action) => {
        const { iterationId, dashboard } = action.payload;
        // Keep selectedIterationId in sync when the server resolved a default
        if (iterationId && !state.selectedIterationId) {
          state.selectedIterationId = iterationId;
        }
        applyDashboardPayload(state, dashboard);
      })
      .addCase(fetchDashboardData.rejected, (state, action) => {
        state.isLoading = false;
        state.error     = action.payload as string;
        if (state.chapterScores.length === 0) state.chapterScores = FALLBACK_CHAPTER_SCORES;
        if (!state.complianceDistribution) state.complianceDistribution = { compliant: 0, partial: 0, non_compliant: 0, na: 0, total: 0 };
      });

    // ── fetchIterations (standalone, used by other pages) ────────────────
    builder
      .addCase(fetchIterations.pending, (state) => {
        state.iterationsLoading = true;
      })
      .addCase(fetchIterations.fulfilled, (state, action) => {
        state.iterationsLoading = false;
        state.iterations = action.payload;
        if (!state.selectedIterationId && action.payload.length > 0) {
          state.selectedIterationId = action.payload[0].iteration_id;
        }
      })
      .addCase(fetchIterations.rejected, (state) => {
        state.iterationsLoading = false;
      });
  },
});

export const { clearDashboardError, setSelectedIteration } = dashboardSlice.actions;
export default dashboardSlice.reducer;